import {
  GetUserProfilesResponse,
  InsertUserProfileInput,
  UpdateUserProfileResponse,
  UserProfileItem,
} from "@/src/models/user.model";
import { USER_PROFILE_QUERY_KEYS } from "@/src/query-keys/users.keys";
import { decodeToken } from "@/src/services/api/auth.api";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Toast from "react-native-toast-message";
import gqlClient from "../graphql.client";
import {
  GET_ALL_USER_PROFILES,
  GET_USER_ARCHIVED_EVENT_IDS,
  GET_USER_PROFILES,
  UPDATE_USER_ARCHIVED_EVENT_IDS,
  UPDATE_USER_PROFILE,
} from "./profile.query";

const useInvalidateUserProfiles = () => {
  const queryClient = useQueryClient();
  return () =>
    queryClient.invalidateQueries({ queryKey: USER_PROFILE_QUERY_KEYS.base });
};

// fetch profile by userId
export const useGetUserProfileQuery = (userId: string) =>
  useQuery<UserProfileItem | null>({
    queryKey: USER_PROFILE_QUERY_KEYS.byUserId(userId),
    queryFn: async () => {
      const client = await gqlClient();
      const data = await client.request<GetUserProfilesResponse>(
        GET_USER_PROFILES,
        {
          where: { CreatedBy: { eq: userId } },
          order: [],
          pageNo: 1,
          pageSize: 1,
        },
      );
      return data.getuserprofiles.items[0] ?? null;
    },
    enabled: !!userId,
  });

export const useUpdateUserProfileMutation = () => {
  const invalidate = useInvalidateUserProfiles();
  return useMutation({
    mutationFn: async ({
      input,
      userId,
    }: {
      input: InsertUserProfileInput;
      userId: string;
    }) => {
      const client = await gqlClient();
      const data = await client.request<UpdateUserProfileResponse>(
        UPDATE_USER_PROFILE,
        {
          createdBy: userId,
          ...input,
        },
      );
      return data.updateuserprofile;
    },
    onSuccess: () => {
      invalidate();
      Toast.show({ type: "success", text1: "SUCCESS_PROFILE_UPDATE" });
    },
    onError: () => {
      Toast.show({ type: "error", text1: "ERROR_PROFILE_UPDATE" });
    },
  });
};

export const useGetUserArchivedEventIdsQuery = (CreatedBy: string) =>
  useQuery({
    queryKey: USER_PROFILE_QUERY_KEYS.archivedEventIds(CreatedBy),
    queryFn: async () => {
      const client = await gqlClient();
      const data = await client.request(GET_USER_ARCHIVED_EVENT_IDS, {
        where: { CreatedBy: { eq: CreatedBy } },
        order: [],
        pageNo: 1,
        pageSize: 1,
      });
      return data.getuserprofiles.items[0]?.archivedEventIds ?? [];
    },
    enabled: !!CreatedBy,
  });

export const useUpdateUserArchivedEventIdsMutation = () => {
  const invalidate = useInvalidateUserProfiles();
  return useMutation({
    mutationFn: async ({
      CreatedBy,
      archivedEventIds,
    }: {
      CreatedBy: string;
      archivedEventIds: string[];
    }) => {
      const client = await gqlClient();
      const data = await client.request(UPDATE_USER_ARCHIVED_EVENT_IDS, {
        createdBy: CreatedBy,
        archivedEventIds,
      });
      return data.updateuserprofile;
    },
    onSuccess: () => {
      invalidate();
      Toast.show({ type: "success", text1: "SUCCESS_ARCHIVED_EVENT" });
    },
    onError: () => {
      Toast.show({ type: "error", text1: "ERROR_ARCHIVED_EVENT" });
    },
  });
};

export const useGetAllUserProfilesQuery = () =>
  useQuery({
    queryKey: [...USER_PROFILE_QUERY_KEYS.base, "birthdays"],
    queryFn: async () => {
      const client = await gqlClient();
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const currentMonth = String(today.getMonth() + 1).padStart(2, "0");
      const nextMonthDate = new Date(today);
      nextMonthDate.setDate(1);
      nextMonthDate.setMonth(today.getMonth() + 1); // handles Dec → Jan rollover
      const nextMonth = String(nextMonthDate.getMonth() + 1).padStart(2, "0");

      const orgId = (await decodeToken())?.org_id;
      // Fetch both months in parallel
      const [currentData, nextData] = await Promise.all([
        client.request<GetUserProfilesResponse>(GET_ALL_USER_PROFILES, {
          month: `<${currentMonth}>`,
          orgId,
        }),
        client.request<GetUserProfilesResponse>(GET_ALL_USER_PROFILES, {
          month: `<${nextMonth}>`,
          orgId,
        }),
      ]);

      const allItems = [
        ...currentData.getuserprofiles.items,
        ...nextData.getuserprofiles.items,
      ];
      const seen = new Set<string>();
      return allItems.filter((item) => {
        if (seen.has(item.ItemId)) return false;
        seen.add(item.ItemId);
        return true;
      });
    },
  });

export const useToggleSaved = (userId: string, type: "news" | "wiki") => {
  const queryClient = useQueryClient();
  const { data: profile } = useGetUserProfileQuery(userId);

  const savedKey = type === "news" ? "newsSaved" : "wikiSaved";

  return useMutation({
    mutationFn: async (articleId: string) => {
      const current: string[] = Array.isArray(profile?.[savedKey])
        ? [...(profile[savedKey] as string[])]
        : [];
      const updated = current.includes(articleId)
        ? current.filter((id) => id !== articleId)
        : [...current, articleId];

      const client = await gqlClient();
      await client.request(UPDATE_USER_PROFILE, {
        createdBy: userId,
        [savedKey]: updated,
      });
    },
    onMutate: async (articleId: string) => {
      await queryClient.cancelQueries({
        queryKey: USER_PROFILE_QUERY_KEYS.byUserId(userId),
      });

      const previousProfile = queryClient.getQueryData(
        USER_PROFILE_QUERY_KEYS.byUserId(userId),
      );

      queryClient.setQueryData(
        USER_PROFILE_QUERY_KEYS.byUserId(userId),
        (old: any) => {
          if (!old) return old;
          const current: string[] = Array.isArray(old[savedKey])
            ? old[savedKey]
            : [];
          const updated = current.includes(articleId)
            ? current.filter((id) => id !== articleId)
            : [...current, articleId];
          return { ...old, [savedKey]: updated };
        },
      );

      return { previousProfile };
    },
    onError: (_err, _articleId, context) => {
      if (context?.previousProfile) {
        queryClient.setQueryData(
          USER_PROFILE_QUERY_KEYS.byUserId(userId),
          context.previousProfile,
        );
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: USER_PROFILE_QUERY_KEYS.byUserId(userId),
      });
    },
  });
};
